import { API_BASE_URL, TOKEN_STORAGE_KEY } from '@/constants';
import { ApiResponse } from '@/types';

export class ApiError extends Error {
  public statusCode: number;
  public details?: any;

  constructor(statusCode: number, message: string, details?: any) {
    super(message);
    this.name = 'ApiError';
    this.statusCode = statusCode;
    this.details = details;
  }
}

class ApiClient {
  private baseURL: string;
  private token: string | null = null;
  private agronodKontoId: string | null = null;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
  }

  /**
   * Set authentication token explicitly (for OIDC tokens).
   * Takes precedence over localStorage token.
   */
  public setToken(token: string | null): void {
    this.token = token;
  }

  /**
   * Set the selected agronodkonto ID to be sent on every request.
   * Called synchronously during render by useApiClient alongside setToken.
   */
  public setAgronodKontoId(id: string | null): void {
    this.agronodKontoId = id;
  }

  private getAuthToken(): string | null {
    // Prioritize explicitly set token (OIDC) over localStorage
    return this.token || localStorage.getItem(TOKEN_STORAGE_KEY);
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    const token = this.getAuthToken();

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string> || {}),
    };

    // Add authorization header if token exists
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    if (this.agronodKontoId) {
      headers['X-Agronodkonto-Id'] = this.agronodKontoId;
    }

    // Remove Content-Type for FormData
    if (options.body instanceof FormData) {
      delete headers['Content-Type'];
    }

    const config: RequestInit = {
      ...options,
      headers,
    };

    try {
      const response = await fetch(url, config);
      // Handle different response types
      let responseData: ApiResponse<T>;

      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        responseData = await response.json();
      } else {
        // Handle non-JSON responses (like file downloads)
        const text = await response.text();
        responseData = {
          success: response.ok,
          data: text as unknown as T,
          message: response.statusText,
        };
      }

      if (!response.ok) {
        throw new ApiError(
          response.status,
          responseData.message || response.statusText,
          responseData.errors || responseData.data
        );
      }

      return responseData.data as T;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }

      // Handle network errors
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new ApiError(0, 'Nätverksfel. Kontrollera din internetanslutning.');
      }

      throw new ApiError(500, 'Ett oväntat fel uppstod.');
    }
  }

  async get<T>(endpoint: string, params?: Record<string, any>): Promise<T> {
    const url = params ? `${endpoint}?${new URLSearchParams(params)}` : endpoint;
    return this.request<T>(url, { method: 'GET' });
  }

  async post<T>(endpoint: string, data?: any): Promise<T> {
    const body = data instanceof FormData ? data : JSON.stringify(data);
    return this.request<T>(endpoint, {
      method: 'POST',
      body,
    });
  }

  async put<T>(endpoint: string, data?: any): Promise<T> {
    const body = data instanceof FormData ? data : JSON.stringify(data);
    return this.request<T>(endpoint, {
      method: 'PUT',
      body,
    });
  }

  async patch<T>(endpoint: string, data?: any): Promise<T> {
    const body = data instanceof FormData ? data : JSON.stringify(data);
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body,
    });
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  // File download with proper headers
  async download(endpoint: string, _filename?: string): Promise<Blob> {
    const url = `${this.baseURL}${endpoint}`;
    const token = this.getAuthToken();

    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    if (this.agronodKontoId) {
      headers['X-Agronodkonto-Id'] = this.agronodKontoId;
    }

    const response = await fetch(url, { headers });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new ApiError(
        response.status,
        errorData.message || response.statusText,
        errorData
      );
    }

    return response.blob();
  }

  // Upload files with progress tracking
  async uploadWithProgress<T>(
    endpoint: string,
    formData: FormData,
    onProgress?: (progress: number) => void
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const token = this.getAuthToken();

      xhr.open('POST', `${this.baseURL}${endpoint}`);

      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      }

      if (this.agronodKontoId) {
        xhr.setRequestHeader('X-Agronodkonto-Id', this.agronodKontoId);
      }

      // Track upload progress
      if (onProgress) {
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            const progress = (event.loaded / event.total) * 100;
            onProgress(progress);
          }
        });
      }

      xhr.onload = () => {
        try {
          const response = JSON.parse(xhr.responseText);

          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(response.data);
          } else {
            reject(new ApiError(xhr.status, response.message, response.errors));
          }
        } catch (error) {
          reject(new ApiError(xhr.status, 'Invalid response format'));
        }
      };

      xhr.onerror = () => {
        reject(new ApiError(0, 'Nätverksfel vid filuppladdning'));
      };

      xhr.send(formData);
    });
  }

  // Health check
  async health(): Promise<{ status: string; timestamp: string }> {
    try {
      const response = await fetch(`${this.baseURL}/health/liveness`);
      return response.json();
    } catch (error) {
      throw new ApiError(0, 'Health check failed');
    }
  }
}

// Create and export API client instance
export const apiClient = new ApiClient(API_BASE_URL);

// Export the class for creating additional instances if needed
export { ApiClient };

// Utility function to handle API errors consistently
export const handleApiError = (error: unknown): string => {
  if (error instanceof ApiError) {
    // If message is an object, try to extract a readable message
    if (typeof error.message === 'object' && error.message !== null) {
      const msgObj = error.message as Record<string, unknown>;
      if (typeof msgObj['message'] === 'string') {
        return msgObj['message'];
      }
      if (typeof msgObj['error'] === 'string') {
        return msgObj['error'];
      }
      return 'Ett fel uppstod vid uppladdning';
    }
    return error.message;
  }

  if (error instanceof Error) {
    // Handle cases where error.message might be stringified JSON
    try {
      const parsed = JSON.parse(error.message);
      if (typeof parsed === 'object' && parsed.message) {
        return parsed.message;
      }
    } catch {
      // Not JSON, return as-is
    }
    return error.message;
  }

  // Handle plain objects
  if (typeof error === 'object' && error !== null) {
    const errObj = error as Record<string, unknown>;
    if (typeof errObj['message'] === 'string') {
      return errObj['message'];
    }
    if (typeof errObj['error'] === 'string') {
      return errObj['error'];
    }
  }

  return 'Ett oväntat fel uppstod';
};

// Utility function to check if an error is an authorization error
export const isAuthError = (error: unknown): boolean => {
  return error instanceof ApiError && error.statusCode === 401;
};

// Utility function to check if an error is a network error
export const isNetworkError = (error: unknown): boolean => {
  return error instanceof ApiError && error.statusCode === 0;
};